import { NoData } from "@";
import PaginationComponent from "@/shared/components/dataTable/Pagination";
import type { FC } from "react";
import {
  cardEmptySurface,
  cardFooterBar,
  cardGridGap,
  cardGridStack,
} from "../../shared/cards";
import type { CarouselItem, PaginationState } from "../types";
import { CarouselCard, CarouselCardSkeleton } from "./CarouselCard";

// Pagination is a (.jsx) component with loosely-typed callback props; give it
// a precise signature here so the call site type-checks cleanly.
const Pagination = PaginationComponent as unknown as FC<{
  localPg?: boolean;
  totalRows: number;
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  rowsPerPageOptions?: (number | "all")[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (size: number) => void;
}>;

interface Props {
  data: CarouselItem[];
  isLoading: boolean;
  totalItems: number;
  pg: PaginationState;
  updatePg: (p: Partial<PaginationState>) => void;
  onEdit: (item: CarouselItem) => void;
  deleteHandler: (id: number) => Promise<void> | void;
}

const GRID_CLASS = `grid grid-cols-1 ${cardGridGap} sm:grid-cols-2 xl:grid-cols-4`;

export const CarouselGrid = ({
  data,
  isLoading,
  totalItems,
  pg,
  updatePg,
  onEdit,
  deleteHandler,
}: Props) => {
  if (isLoading) {
    return (
      <div className={GRID_CLASS}>
        {Array.from({ length: 4 }).map((_, i) => (
          <CarouselCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className={cardEmptySurface}>
        <NoData />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalItems / pg.size));

  return (
    <div className={cardGridStack}>
      <div className={GRID_CLASS}>
        {data.map((item) => (
          <CarouselCard
            key={item.id}
            item={item}
            onEdit={onEdit}
            deleteHandler={deleteHandler}
          />
        ))}
      </div>

      {totalItems > 4 && (
        <div className={cardFooterBar}>
          <Pagination
            localPg={false}
            totalRows={totalItems}
            currentPage={pg.page}
            totalPages={totalPages}
            rowsPerPage={pg.size}
            onPageChange={(page: number) => updatePg({ page })}
            onRowsPerPageChange={(size: number) => updatePg({ size, page: 1 })}
          />
        </div>
      )}
    </div>
  );
};
